import React, { useState, useEffect, useContext, Children } from "react";
import styled from "styled-components";
import Request from "common/Request";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import ActionsContext from "common/ActionsContext";
import {
  getDirectValue,
  asUnknown,
  getTupleValues,
  getFirstRowValuesMatching,
  getFirstRowValues,
  getTwoColumnTupleValues,
} from "./api";

const FactLayout = styled.div`
  display: flex;
  flex-flow: row wrap;
  .fact-label,
  .fact-value {
    padding: 0 15px 5px;
  }
  .fact-label {
    flex: 30% 0 0;
  }
  .fact-value {
    flex: 60% 1 1;
  }
`;
const Fact = ({ label, value, transform = undefined }) => (
  <FactLayout>
    <div className="fact-label">{label}</div>
    <div className="fact-value font-bold db-counters">
      {transform ? transform(value) : value}
    </div>
  </FactLayout>
);

const RequestOfFacts = ({ retrieval, children = [] }) => {
  const actions = useContext(ActionsContext);
  const [request, setRequest] = useState(null);
  const doLoad = () => {
    setRequest(retrieval());
  };
  useEffect(() => doLoad() || actions.recv("do-load", doLoad), []);
  return (
    <Request during={request} keepContentWhileLoading={true}>
      {(result) =>
        Children.map(children, (child) =>
          React.cloneElement(child, {
            value: child.props.field ? result[child.props.field] : result,
          })
        )
      }
    </Request>
  );
};

const loadHostName = () =>
  ifCl
    .run("show system hostname")
    .then(getDirectValue)
    .catch(asUnknown("value"));

const HostNameFacts = () => (
  <RequestOfFacts retrieval={loadHostName}>
    <Fact label="System name" field="value" />
  </RequestOfFacts>
);
const loadVersion = () =>
  ifCl.run("show version").then(getTupleValues).catch(asUnknown("BQNVersion"));
const VersionFacts = () => (
  <RequestOfFacts retrieval={loadVersion}>
    <Fact label="BQN release" field="BQNRelease" />
  </RequestOfFacts>
);

const loadHardwareChassis = () =>
  ifCl
    .run("show hardware chassis")
    .then(getTupleValues)
    .catch(asUnknown("BQNHardwareChassis"));
const HardwareChassisFacts = () => (
  <RequestOfFacts retrieval={loadHardwareChassis}>
    <Fact label="Vendor" field="Vendor" />
    <Fact label="Product" field="Product" />
    <Fact
      label="Serial number"
      field="Serial"
      transform={(value) => (value === "0" ? "n/a" : value)}
    />
  </RequestOfFacts>
);
const loadHardwareID = () =>
  ifCl.run("show hardware id").then(getFirstRowValues);
const HardwareIDFacts = () => (
  <RequestOfFacts retrieval={loadHardwareID}>
    <Fact label="Hardware ID" field="SYSTEM-ID" />
  </RequestOfFacts>
);

const objectMiss = (target, ...fields) =>
  fields.find((field) => Object.keys(target).includes(field) !== true) !==
  undefined;

const cpuDisposition = (data) =>
  objectMiss(data, "CPUs", "Sockets", "CoresPerSocket", "ThreadsPerCore")
    ? "unknown"
    : `${data["CPUs"]} (${data["Sockets"]}x${data["CoresPerSocket"]}x${data["ThreadsPerCore"]})`;

const loadHardwareCPU = () =>
  ifCl.run("show hardware cpu").then(getTupleValues);
const HardwareCPUFacts = () => (
  <RequestOfFacts retrieval={loadHardwareCPU}>
    <Fact label="CPU model" field="ModelName" />
    <Fact label="CPU cores" transform={cpuDisposition} />
  </RequestOfFacts>
);

const loadHardwareMemory = () =>
  ifCl.run("show hardware memory").then(getTupleValues);
const HardwareMemoryFacts = () => (
  <RequestOfFacts retrieval={loadHardwareMemory}>
    <Fact label="Memory size" field="MemorySize" />
  </RequestOfFacts>
);
const usedAndAvailable = (data) =>
  objectMiss(data, "Used", "Avail")
    ? "unknown"
    : `${data["Used"]} used / ${data["Avail"]} available`;

const isRoot = (data) => data["Mounted"] === "/";
const loadHardwareFileSystem = () =>
  ifCl.run("show system filesystem").then(getFirstRowValuesMatching(isRoot));
const HardwareFileSystemFacts = () => (
  <RequestOfFacts retrieval={loadHardwareFileSystem}>
    <Fact label="Root dist partition" transform={usedAndAvailable} />
  </RequestOfFacts>
);

const percentFormat = (value) => `${value.toFixed(2)}%`;

const percentageOfDroppedPkgs = (data) =>
  objectMiss(data, "ErrorDroppedPkt", "TotalDnRxPkt", "TotalUpRxPkt") ||
  isNaN(data["ErrorDroppedPkt"]) ||
  data["TotalDnRxPkt"] + data["TotalUpRxPkt"] === 0
    ? "unknown"
    : percentFormat(
        data["ErrorDroppedPkt"] / (data["TotalDnRxPkt"] + data["TotalUpRxPkt"])
      );

const loadNetwork = () =>
  ifCl.run("show pkteng statistics extended").then(getTwoColumnTupleValues);
const NetworkFacts = () => (
  <RequestOfFacts retrieval={loadNetwork}>
    <Fact label="Dropped pkts" transform={percentageOfDroppedPkgs} />
  </RequestOfFacts>
);

const Report = () => (
  <ColumnsGrid columns={2} minWidth="30rem" rowGap="0" gap="15px">
    <HostNameFacts />
    <VersionFacts />
    <HardwareChassisFacts />
    <HardwareIDFacts />
    <HardwareCPUFacts />
    <HardwareMemoryFacts />
    <HardwareFileSystemFacts />
    <NetworkFacts />
  </ColumnsGrid>
);

export default Report;
